import type {
  Channel,
  ChannelCreateInput,
  ChannelUpdateInput,
  HomeData,
  Page,
  PageCreateInput,
  PageUpdateInput,
  SummaryItem,
} from '@regia/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, http } from '../../lib/api';
import { keys } from '../../lib/query';

export function usePages(archived = false) {
  return useQuery({
    queryKey: keys.pages(archived),
    queryFn: () => http.get<Page[]>(`/api/pages${archived ? '?archived=1' : ''}`),
  });
}

export function usePage(ref: string | undefined) {
  return useQuery({
    queryKey: keys.page(ref ?? ''),
    queryFn: () => http.get<Page>(`/api/pages/${encodeURIComponent(ref ?? '')}`),
    enabled: Boolean(ref),
  });
}

export function useHome() {
  return useQuery({ queryKey: keys.home, queryFn: () => http.get<HomeData>('/api/home') });
}

export function useSummary(pageId: string) {
  return useQuery({
    queryKey: keys.summary(pageId),
    queryFn: () => http.get<SummaryItem[]>(`/api/pages/${pageId}/summary`),
  });
}

function useInvalidatePages() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['pages'] });
    qc.invalidateQueries({ queryKey: ['page'] });
    qc.invalidateQueries({ queryKey: keys.home });
  };
}

export function useCreatePage() {
  const invalidate = useInvalidatePages();
  return useMutation({
    mutationFn: (input: PageCreateInput) => http.post<Page>('/api/pages', input),
    onSuccess: invalidate,
  });
}

export function useUpdatePage(id: string) {
  const invalidate = useInvalidatePages();
  return useMutation({
    mutationFn: (input: PageUpdateInput) => http.patch<Page>(`/api/pages/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeletePage(id: string) {
  const invalidate = useInvalidatePages();
  return useMutation({ mutationFn: () => http.del(`/api/pages/${id}`), onSuccess: invalidate });
}

export function useReorderPages() {
  const invalidate = useInvalidatePages();
  return useMutation({
    mutationFn: (ids: string[]) => http.post('/api/pages/reorder', { ids }),
    onSuccess: invalidate,
  });
}

export function useUploadLogo(id: string) {
  const invalidate = useInvalidatePages();
  return useMutation({
    mutationFn: (file: File) =>
      api<Page>(`/api/pages/${id}/logo`, {
        method: 'PUT',
        body: file,
        headers: { 'content-type': file.type || 'image/jpeg' },
      }),
    onSuccess: invalidate,
  });
}

export function useRemoveLogo(id: string) {
  const invalidate = useInvalidatePages();
  return useMutation({
    mutationFn: () => http.del<Page>(`/api/pages/${id}/logo`),
    onSuccess: invalidate,
  });
}

export function useAddChannel(pageId: string) {
  const invalidate = useInvalidatePages();
  return useMutation({
    mutationFn: (input: ChannelCreateInput) =>
      http.post<Channel>(`/api/pages/${pageId}/channels`, input),
    onSuccess: invalidate,
  });
}

export function useUpdateChannel() {
  const invalidate = useInvalidatePages();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ChannelUpdateInput }) =>
      http.patch<Channel>(`/api/channels/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useRemoveChannel() {
  const invalidate = useInvalidatePages();
  return useMutation({
    mutationFn: (id: string) => http.del(`/api/channels/${id}`),
    onSuccess: invalidate,
  });
}
